import type { ModelInstance } from './instanced-layout';

/**
 * Where a brazier's flame actually is (2026-10-15, QA #15a: "the torch light
 * comes from the base of the torch, not from where the fire burns").
 *
 * The server syncs a brazier as an `x`/`z` on the floor plan — the wall-prop's
 * mounting point, which is all the sim needs for `isLit` and the E range — and
 * the client hung its light at a fixed 1.6m there. A `torch_mounted` sconce is
 * mounted at `TORCH_SCONCE_HEIGHT_M` (2.2m) and its flame sits another ~0.5m up
 * and ~0.33m out from the wall, so the light was over a metre below the fire
 * and inside the bracket: a hot spot on the masonry under the torch.
 *
 * The layout's own model instances already say where each sconce is and which
 * way it faces, so the flame is found from those rather than by widening the
 * schema. Offsets are measured from the models' vertex data (centroid of the
 * top 0.3m of each mesh) — local space, +z out of the wall.
 */
export const FLAME_OFFSETS: Readonly<Record<string, { y: number; out: number }>> = {
  torch_mounted: { y: 0.52, out: 0.33 },
  torch: { y: 0.52, out: 0 },
  torch_lit: { y: 0.56, out: 0 },
  candle_lit: { y: 0.86, out: 0 },
};

/** Where the light went before this existed, kept for a brazier no layout instance matches. */
export const FALLBACK_FLAME_Y_M = 1.6;

/** How far a brazier's synced position may sit from its model's origin and still be called the same torch. */
const MATCH_RADIUS_M = 0.75;

export function flamePosition(
  x: number, z: number, instances: readonly ModelInstance[],
): { x: number; y: number; z: number } {
  let best: ModelInstance | null = null;
  let bestDist = MATCH_RADIUS_M;
  for (const inst of instances) {
    if (!(inst.model in FLAME_OFFSETS)) continue;
    const d = Math.hypot(inst.x - x, inst.z - z);
    if (d <= bestDist) { best = inst; bestDist = d; }
  }
  if (!best) return { x, y: FALLBACK_FLAME_Y_M, z };
  const offset = FLAME_OFFSETS[best.model];
  // Same convention as `buildInstancedLayout`'s makeRotationY: local +z maps to
  // (sin r, cos r) in world x/z.
  return {
    x: best.x + Math.sin(best.rotationY) * offset.out,
    y: best.y + offset.y,
    z: best.z + Math.cos(best.rotationY) * offset.out,
  };
}
